import LoadingErrorWrapper from '../shared_components/loadingErrorWrapper';
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useFeatureUsage, PRODUCT_AREAS } from '../../hooks/useFeatureUsage';
import { 
  Box, 
  Grid, 
  Typography, 
  Link,
  Chip,
  IconButton
} from '@mui/material';
import { useLocation } from 'react-router-dom';


import AutoGraphIcon from '@mui/icons-material/AutoGraph';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import MapsHomeWorkIcon from '@mui/icons-material/MapsHomeWork';
import GetApp from '@mui/icons-material/GetApp';

// Import visualization components
import ProjectXProjectionsChart from '../projections_explorer/dashboard/ProjectXProjectionsChart';
import CompXCommunityPlot from '../competitive_explorer/community/CompXCommunityPlot';
import UnifiedSchoolMap from '../shared_components/unifiedSchoolMap';
import GeoXNearbyMapPopup from '../geographic_explorer/nearby/GeoXNearbyMapPopup';
import GeoXEnrollmentCompositionChanges from '../geographic_explorer/enrollment/GeoXEnrollmentCompositionChanges';
import GeoXCommunityTrends from '../geographic_explorer/community/GeoXCommunityTrends';
import GeoXCommunityDemographics from '../geographic_explorer/community/GeoXCommunityDemographics';

// Shared Components
import PageLayout from '../shared_components/pageLayout';
import InitialSchoolSelector from '../shared_components/initialSchoolSelector';
import withIndexedDBErrorHandler from '../shared_components/withIndexedDBErrorHandler';
import AISummaryAccordion from '../shared_components/AISummaryAccordion';
import { useInsight } from '../../contexts/InsightContext';

// Display Components
import SchoolSummaryFilters from './SchoolSummaryFilters';
import MarketShareAnalysis from './display_components/MarketShareAnalysis';
import DemographicAnalysis from './display_components/DemographicAnalysis';
import ProjectionsAnalysis from './display_components/ProjectionsAnalysis';
import PopulationAnalysis from './display_components/PopulationAnalysis';
import ComparisonAnalysis from './display_components/ComparisonAnalysis';
import SimplePopulationTable from './display_components/SimplePopulationTable';
import { 
  PopulationTrendsSkeleton,
  ProjectionsSkeleton,
  MarketShareSkeleton,
  ComparisonSkeleton,
  DemographicsSkeleton
} from './loading_components/SkeletonLoaders';
import { HeadlinesColumnSkeleton } from './loading_components/HeadlineSkeletons';
import { HeadlineBoxWithLoading } from './loading_components/HeadlineLoadingComponents';

// Store
import useSchoolStore from '../../stores/schoolStore';
import useSchoolMetricsStore from '../../stores/schoolMetricsStore';

// Cache and API
import { useTeamSchoolCache } from '../../hooks/teamCacheService';
import { schoolsApi } from '../../utils/apiService';

// Utils
import { 
  filterSchools,
  calculateEnrollmentTrends,
  getSchoolGrades,
  calculateEnrollment,
  hasGradeOverlap,
  calculateGradeFilteredPopulation,
  formatGrade,
  formatSchoolName,
} from '../../utils/schoolUtils';
import { calculateMarketShareStats } from '../../utils/marketShareUtils';
import chartAnimationState from '../../utils/chartAnimationControl';
import { detectSchoolDataType } from '../../utils/schoolDataDetection';
import { ESRI_CENSUS_YEAR, ESRI_CURRENT_YEAR } from '../../constants/esriDataVintage';
import { BASELINE_STATUS_SHORT_LABELS } from '../../constants/ringMembership';
import { calculateAreaDemographics } from '../../utils/areaCalculations';


// Auth
import { useAuth } from '../../auth/AuthContext';

// Constants to prevent unnecessary rerenders
const EMPTY_TARGETS = {};
const DEFAULT_SCENARIOS = ['low', 'median', 'high'];
const CARD_PROPS_NO_ELEVATION = {
  elevation: 0,
  sx: { 
    height: '100%',
    boxShadow: 'none'
  }
};
const CARD_PROPS_NO_BORDER = {
  elevation: 0,
  sx: { 
    height: '100%',
    boxShadow: 'none',
    border: 'none',
    '& .MuiBox-root': {
      px: 0
    }
  }
};

const SchoolSummary = () => {
  const trackUsage = useFeatureUsage(PRODUCT_AREAS.SCHOOL_EXPLORER);

  const getOrdinalSuffix = (number) => {
    const j = number % 10;
    const k = number % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  // State declarations
  const [selectedDriveTime, setSelectedDriveTime] = useState(10);
  const [selectedHeadline, setSelectedHeadline] = useState(0);
  const [focusedSchool, setFocusedSchool] = useState(null);
  const [selectedGrades, setSelectedGrades] = useState([]);
  const [governanceFilter, setGovernanceFilter] = useState(['Charter', 'District', 'Private']);
  const [schoolTypeFilter, setSchoolTypeFilter] = useState([]);
  const initialDropAPinGradesSet = useRef(false);
  const gradesInitializedForSchoolRef = useRef(null);

  const setSchoolMetrics = useSchoolMetricsStore(state => state.setSchoolMetrics);

  const [sectionLoadingStates, setSectionLoadingStates] = useState({
    population: { loading: false, metricsLoading: false },
    projections: { loading: false, metricsLoading: false },
    marketShare: { loading: false, metricsLoading: false },
    comparison: { loading: false, metricsLoading: false },
    demographics: { loading: false, metricsLoading: false }
  });

  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const schoolId = params.get('school');
  const currentSchoolIdRef = useRef(null);


  // Get data from team school cache
  const { 
    schools, 
    loading: schoolsLoading, 
  } = useTeamSchoolCache();

  // Get data from school store
  const {
    selectedSchool,
    esriData,
    error,
    nearbySchools,
    schoolsEnrollmentData,
    enrollmentProjections,
    availableTravelModes,
    selectedTravelMode,
    changeTravelMode,
  } = useSchoolStore();

  // Derive which drive times actually have data for the selected school
  const availableDriveTimes = useMemo(() => {
    if (!esriData?.drive_times) return null;
    const times = Object.keys(esriData.drive_times).map(Number).sort((a, b) => a - b);
    return times.length > 0 ? times : null;
  }, [esriData]);

  // Auto-reset drive time when the available options change and current selection isn't in the list
  useEffect(() => {
    if (availableDriveTimes && !availableDriveTimes.includes(selectedDriveTime)) {
      const fallback = availableDriveTimes.includes(10) ? 10 : availableDriveTimes[0];
      setSelectedDriveTime(fallback);
    }
  }, [availableDriveTimes]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to map section indices to section keys
  const getInsightSection = useCallback((index) => {
    const { isDropAPinLocation } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);
    
    if (isDropAPinLocation) {
      // For drop-a-pin locations, the array is [population, comparison, demographics]
      switch(index) {
        case 0: return 'population';
        case 1: return 'comparison';
        case 2: return 'demographics';
        default: return 'population';
      }
    } else {
      // For regular schools, the array is [population, projections, market-share, comparison, demographics]
      switch(index) {
        case 0: return 'population';
        case 1: return 'projections';
        case 2: return 'market-share';
        case 3: return 'comparison';
        case 4: return 'demographics';
        default: return 'population';
      }
    }
  }, [selectedSchool, schoolsEnrollmentData]);

  const isSectionDataLoaded = useCallback((sectionIndex) => {
  // Detect if this is a location without enrollment data
  const { isDropAPinLocation } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);
  
  // Debug logging to understand what's happening
  // Get the actual section key based on current index mapping
  let sectionKey;
  if (isDropAPinLocation) {
    // For drop-a-pin locations, the array is [population, comparison, demographics]
    switch(sectionIndex) {
      case 0: sectionKey = 'population'; break;
      case 1: sectionKey = 'comparison'; break;
      case 2: sectionKey = 'demographics'; break;
      default: sectionKey = 'population'; break;
    }
  } else {
    // For regular schools, the array is [population, projections, market-share, comparison, demographics]
    switch(sectionIndex) {
      case 0: sectionKey = 'population'; break;
      case 1: sectionKey = 'projections'; break;
      case 2: sectionKey = 'market-share'; break;
      case 3: sectionKey = 'comparison'; break;
      case 4: sectionKey = 'demographics'; break;
      default: sectionKey = 'population'; break;
    }
  }
  
  let result;
  switch (sectionKey) {
    case 'population':
      result = !!esriData;
      break;
    case 'projections':
      if (isDropAPinLocation) {
        // Hide projections section completely for drop-a-pin locations
        result = false;
      } else {
        // Consider projections "loaded" if we have data OR if we've explicitly determined there's no data
        result = enrollmentProjections !== undefined;
      }
      break;
    case 'market-share':
      if (isDropAPinLocation) {
        // Hide market share section completely for drop-a-pin locations
        result = false;
      } else {
        // For schools with enrollment data, we need both ESRI data and enrollment data
        result = !!esriData && schoolsEnrollmentData.length > 0;
      }
      break;
    case 'comparison':
      if (isDropAPinLocation) {
        // For location-only scenarios, we consider it "loaded" even if nearby schools are unavailable
        // The component will handle showing an appropriate message
        result = true;
      } else {
        // For schools with enrollment data, we need both nearby schools and enrollment data
        result = !!nearbySchools && schoolsEnrollmentData.length > 0;
      }
      break;
    case 'demographics':
      if (isDropAPinLocation) {
        // For location-only scenarios, we need ESRI data but can handle missing nearby schools
        // The component will show appropriate messaging for demographics based on available data
        result = !!esriData;
      } else {
        // For schools with enrollment data, we need ESRI data and enrollment data
        result = !!esriData && schoolsEnrollmentData.length > 0;
      }
      break;
    default: 
      result = false;
  }
  
  return result;
}, [esriData, enrollmentProjections, nearbySchools, schoolsEnrollmentData, selectedSchool]);
  

  // Improved isSectionLoading function
  const isSectionLoading = useCallback((index) => {
    const sectionKey = getInsightSection(index);
    return sectionLoadingStates[sectionKey]?.loading || 
           sectionLoadingStates[sectionKey]?.metricsLoading;
  }, [sectionLoadingStates, getInsightSection]);

  // Helper function to check if metrics for a section are loading
  const areMetricsLoading = (index) => {
    const sectionKey = getInsightSection(index);
    
    // Detect if this is a location without enrollment data
    const { isDropAPinLocation } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);
    
    // For drop-a-pin locations, sections 1 and 2 should never show as loading (they're hidden)
    if (isDropAPinLocation && (index === 1 || index === 2)) {
      return false;
    }
    
    // Consider section metrics loading if explicitly marked as loading
    if (sectionLoadingStates[sectionKey]?.metricsLoading === true) {
      return true;
    }
    
    // For location-only schools, use our data availability logic instead of checking for specific data
    if (isDropAPinLocation) {
      // For location-only schools, defer to our isSectionDataLoaded logic
      return !isSectionDataLoaded(index);
    }
    
    // For regular schools, check if we have actual metrics data
    switch(index) {
      case 0: // Population
        return !populationTotals?.current;
      case 1: // Projections
        // Only show loading if we don't have projections AND we're actively loading
        // If enrollmentProjections is explicitly null, don't show loading
        return enrollmentProjections === undefined;
      case 2: // Market Share
        return !marketShareData?.current;
      case 3: // Comparison
        // Keyed on the trends object, not the figure: a school with no baseline has a
        // null change and is finished loading, not still waiting.
        return !selectedSchoolTrends;
      case 4: // Demographics
        // For demographics, only consider it loading if we don't have selectedSchoolTrends at all
        // If we have selectedSchoolTrends but no largestGroup, that means data is unavailable (not loading)
        return !selectedSchoolTrends;
      default:
        return false;
    }
  };

  // Filter schools for comparison (from CompetitiveExplorer)
  const filteredSchools = useMemo(() => {
    if (!selectedSchool || !nearbySchools) return [];
    
    const schoolsForSelectedDriveTime = nearbySchools?.current?.[selectedDriveTime] || [];
    
    const allSchools = [
      { 
        ...selectedSchool,
        drive_time: 0 
      }, 
      ...schoolsForSelectedDriveTime.map(school => ({
        ...school,
        drive_time: selectedDriveTime
      }))
    ];

    return filterSchools(
      allSchools,
      {
        selectedGrades,
        governanceFilter,
        schoolTypeFilter
      },
      schoolsEnrollmentData
    );
  }, [
    selectedSchool,
    nearbySchools,
    selectedDriveTime,
    selectedGrades,
    governanceFilter,
    schoolTypeFilter,
    schoolsEnrollmentData
  ]);

  const { user } = useAuth();

  const { setToolAndSection } = useInsight();

  useEffect(() => {
    if (schools.length === 0) {
      useSchoolStore.getState().fetchSchools();
    }
  }, [schools.length]);

  // Handle school selection from URL
  useEffect(() => {
    if (schools.length > 0 && schoolId) {
      // Use ref to track if we've already processed this schoolId
      if (currentSchoolIdRef.current !== schoolId) {
        // Handle both UUID and NCES ID for backward compatibility
        const school = schools.find(s => s.uuid === schoolId || s.ncessch === schoolId);
        if (school) {
          currentSchoolIdRef.current = schoolId;
          useSchoolStore.getState().selectSchool(school);
          
          // Reset loading states when explicitly changing schools
          setSectionLoadingStates({
            population: { loading: false, metricsLoading: false },
            projections: { loading: false, metricsLoading: false },
            marketShare: { loading: false, metricsLoading: false },
            comparison: { loading: false, metricsLoading: false },
            demographics: { loading: false, metricsLoading: false }
          });
        }
      }
    }
  }, [schoolId, schools]);

  useEffect(() => {
    if (selectedSchool) {
      const controller = new AbortController();
      const signal = controller.signal;
      
      // Store the section we're currently loading
      const currentSection = getInsightSection(selectedHeadline);

      // Reset loading state when school changes
        setSectionLoadingStates(prev => ({
          ...prev,
          [currentSection]: {
            loading: false,
            metricsLoading: false
          }
        }));
      
      const loadSectionData = async () => {
        try {
          // Detect data type for conditional loading
          const { isDropAPinLocation } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);
          
          // Check if data is already loaded before setting any loading states
          const dataAlreadyLoaded = isSectionDataLoaded(selectedHeadline);
          
          if (!dataAlreadyLoaded) {
            // First, set metrics as loading immediately
            setSectionLoadingStates(prev => ({
              ...prev,
              [currentSection]: { 
                ...prev[currentSection],
                metricsLoading: true 
              }
            }));
            
            // Then set full content loading after a short delay
            setTimeout(() => {
              setSectionLoadingStates(prev => ({
                ...prev,
                [currentSection]: { 
                  ...prev[currentSection], 
                  loading: true 
                }
              }));
            }, 50);
          }
    
          const startTime = performance.now();
    
          // Only load data for the current section if data isn't already loaded
          if (!dataAlreadyLoaded) {
            switch (selectedHeadline) {
            case 0: // Population Trends
              if (!esriData) {
                await schoolsApi.getMarketShareData(selectedSchool.uuid, { signal });
              }
              break;
  
            case 1: // Projected Enrollment
              // Projections are handled by the store in selectSchool, no additional loading needed
              break;
  
            case 2: // Market Share Analysis
              if (isDropAPinLocation) {
                // For location-only scenarios, we only need ESRI data
                if (!esriData) {
                  await schoolsApi.getMarketShareData(selectedSchool.uuid, { signal });
                }
              } else {
                // For schools with enrollment data, load both ESRI and enrollment data
                await Promise.all([
                  !esriData && schoolsApi.getMarketShareData(selectedSchool.uuid, { signal }),
                  !schoolsEnrollmentData.length && schoolsApi.getSchoolsEnrollmentData(
                    [selectedSchool.uuid],
                    selectedSchool.uuid,
                    { signal }
                  )
                ].filter(Boolean));
              }
              break;
  
            case 3: // Area School Comparison
              if (isDropAPinLocation) {
                // For location-only scenarios, we need nearby schools and their enrollment data
                if (!nearbySchools) {
                  const nearbyData = await schoolsApi.getAllNearbySchools(selectedSchool.uuid);
                  if (nearbyData && !signal.aborted) {
                    // Try to get enrollment data for nearby schools only
                    const nearbySchoolIds = Object.values(nearbyData.current || {})
                      .flatMap(schools => schools.map(s => s.uuid));
                    if (nearbySchoolIds.length > 0) {
                      try {
                        await schoolsApi.getSchoolsEnrollmentData(
                          nearbySchoolIds,
                          selectedSchool.uuid,
                          { signal }
                        );
                      } catch (error) {
                        // It's okay if nearby schools don't have enrollment data
                        console.info('Some nearby schools may not have enrollment data:', error);
                      }
                    }
                  }
                }
              } else {
                // For schools with enrollment data, load all data including selected school
                if (!nearbySchools || !schoolsEnrollmentData.length) {
                  const nearbyData = await schoolsApi.getAllNearbySchools(selectedSchool.uuid);
                  if (nearbyData && !signal.aborted) {
                    const schoolIds = new Set([
                      selectedSchool.uuid,
                      ...Object.values(nearbyData.current || {})
                        .flatMap(schools => schools.map(s => s.uuid))
                    ]);
                    await schoolsApi.getSchoolsEnrollmentData(
                      Array.from(schoolIds),
                      selectedSchool.uuid,
                      { signal }
                    );
                  }
                }
              }
              break;
  
            case 4: // Demographic Composition
              if (isDropAPinLocation) {
                // For location-only scenarios, we need ESRI data and nearby schools
                await Promise.all([
                  !esriData && schoolsApi.getMarketShareData(selectedSchool.uuid, { signal }),
                  !nearbySchools && schoolsApi.getAllNearbySchools(selectedSchool.uuid)
                ].filter(Boolean));
              } else {
                // For schools with enrollment data, load ESRI and enrollment data
                await Promise.all([
                  !esriData && schoolsApi.getMarketShareData(selectedSchool.uuid, { signal }),
                  !schoolsEnrollmentData.length && schoolsApi.getSchoolsEnrollmentData(
                    [selectedSchool.uuid],
                    selectedSchool.uuid,
                    { signal }
                  )
                ].filter(Boolean));
              }
              break;

              default:
              // Either do nothing or handle unexpected cases

              break;
            }
          }
  
          // Calculate how long the data loading took
          const loadTime = performance.now() - startTime;
        
          if (!signal.aborted) {
            // REPLACE THIS SECTION with the new code:
            
            // After data fetch, check if data is actually available before turning off loading
            if (isSectionDataLoaded(selectedHeadline)) {
              // First turn off metrics loading
              setSectionLoadingStates(prev => ({
                ...prev,
                [currentSection]: { 
                  ...prev[currentSection],
                  metricsLoading: false 
                }
              }));
              
              // Keep skeleton visible longer to ensure data is processed
              const minDisplayTime = Math.max(1200 - loadTime, 800);
              
              if (minDisplayTime > 0) {
                await new Promise(resolve => setTimeout(resolve, minDisplayTime));
              }
              
              if (!signal.aborted) {
                setSectionLoadingStates(prev => ({
                  ...prev,
                  [currentSection]: { 
                    ...prev[currentSection],
                    loading: false 
                  }
                }));
              }
            } else {
              // If data still isn't available after loading, keep loading state

            }
            // END of replacement section
          }
        } catch (error) {
          // Error handling
          if (error.name === 'AbortError') {
            return;
          }
          

          
          if (!signal.aborted) {
            setSectionLoadingStates(prev => ({
              ...prev,
              [currentSection]: { 
                metricsLoading: false,
                loading: false 
              }
            }));
          }
        }
      };
    
      loadSectionData();
      
      return () => {
        controller.abort();
      };
    }
  }, [
    selectedSchool, 
    selectedHeadline, 
    esriData, 
    enrollmentProjections, 
    nearbySchools,
    schoolsEnrollmentData,
    isSectionDataLoaded,
    getInsightSection
  ]);

  // Calculate enrollment trends (from GeoXNearbyHeadlines)
  const selectedSchoolTrends = useMemo(() => {
    if (!selectedSchool || !schoolsEnrollmentData?.length) {

      return null;
    }
    
    const selectedSchoolData = schoolsEnrollmentData.find(s => s.uuid === selectedSchool.uuid);
    if (!selectedSchoolData) {

      return null;
    }

    const trends = calculateEnrollmentTrends({
      selectedSchool,
      selectedSchoolData,
      filteredSchools,
      schoolsEnrollmentData,
      selectedGrades,
      governanceFilter
    });

    // Log demographic calculation results for debugging
    if (trends) {

    }

    return trends;
  }, [selectedSchool, schoolsEnrollmentData, filteredSchools, selectedGrades, governanceFilter]);

  // School data for nearby comparisons
  const schoolData = useMemo(() => {
    if (!selectedSchool || !nearbySchools) {
      return [];
    }
    
    const { isDropAPinLocation } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);
    const driveTime = selectedDriveTime;
    const schoolsForSelectedDriveTime = nearbySchools?.current?.[driveTime] || [];
    
    let validSchools;
    
    if (isDropAPinLocation) {
      // For drop-a-pin locations, include nearby schools that have enrollment data for proper calculations
      validSchools = schoolsForSelectedDriveTime
        .filter(school => {
          // Don't include the location itself
          if (school.uuid === selectedSchool.uuid) return false;
          
          // Only include schools with enrollment data for accurate enrollment change calculations
          const schoolData = schoolsEnrollmentData.find(d => d.uuid === school.uuid);
          return schoolData && 
                 schoolData.enrollment_by_grade?.current && 
                 schoolData.enrollment_by_grade?.comparison;
        });
    } else {
      // For regular schools, apply the existing filters
      validSchools = schoolsForSelectedDriveTime
        .filter(school => {
          // First check it's not the selected school
          if (school.uuid === selectedSchool.uuid) return false;
          
          // Then check grade overlap
          const hasOverlap = hasGradeOverlap(school.grade_range, selectedGrades.join(', '));
          if (!hasOverlap) return false;
          
          // Then check for required data
          const schoolData = schoolsEnrollmentData.find(d => d.uuid === school.uuid);
          return schoolData && schoolData.enrollment_by_grade?.current;
        });
    }

    const schoolList = [];
    
    // Only include the selected school if it's a real school (not a drop-a-pin location)
    if (!isDropAPinLocation) {
      schoolList.push({ 
        ...selectedSchool,
        drive_time: 0,
        isMainSchool: true,
        isFocused: false,
        currentEnrollment: calculateEnrollment(
          schoolsEnrollmentData.find(d => d.uuid === selectedSchool.uuid)
            ?.enrollment_by_grade?.current || {},
          selectedGrades
        ),
        previousEnrollment: calculateEnrollment(
          schoolsEnrollmentData.find(d => d.uuid === selectedSchool.uuid)
            ?.enrollment_by_grade?.comparison || {},
          selectedGrades
        )
      });
    }

    // Add all valid schools
    
    schoolList.push(...validSchools.map(school => {
      const schoolEnrollmentData = schoolsEnrollmentData.find(d => d.uuid === school.uuid);
      return {
        ...school,
        drive_time: driveTime,
        isMainSchool: false,
        isFocused: false, // Will be determined by focusedSchool prop in map component
        currentEnrollment: calculateEnrollment(
          schoolEnrollmentData?.enrollment_by_grade?.current || {},
          selectedGrades
        ),
        previousEnrollment: calculateEnrollment(
          schoolEnrollmentData?.enrollment_by_grade?.comparison || {},
          selectedGrades
        )
      };
    }));

    return schoolList;
  }, [selectedSchool, nearbySchools, selectedDriveTime, schoolsEnrollmentData, selectedGrades]);

  // Initial grades: drop-a-pin uses one-time init per school (user can change grade filter afterward).
  // Standard schools still follow grade_range from the roster.
  useEffect(() => {
    if (!selectedSchool) {
      initialDropAPinGradesSet.current = false;
      gradesInitializedForSchoolRef.current = null;
      return;
    }

    const { isDropAPinLocation } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);

    if (isDropAPinLocation) {
      if (gradesInitializedForSchoolRef.current !== selectedSchool.uuid) {
        initialDropAPinGradesSet.current = false;
        gradesInitializedForSchoolRef.current = selectedSchool.uuid;
      }
      if (initialDropAPinGradesSet.current) return;

      if (selectedSchool.grade_range) {
        const schoolGrades = getSchoolGrades(selectedSchool.grade_range);
        if (schoolGrades.length > 0) {
          setSelectedGrades(schoolGrades);
          initialDropAPinGradesSet.current = true;
          return;
        }
      }
      setSelectedGrades([
        'Kindergarten',
        '1',
        '2',
        '3',
        '4',
        '5',
        '6',
        '7',
        '8',
        '9',
        '10',
        '11',
        '12',
      ]);
      initialDropAPinGradesSet.current = true;
      return;
    }

    initialDropAPinGradesSet.current = false;
    gradesInitializedForSchoolRef.current = null;
    if (selectedSchool?.grade_range) {
      const schoolGrades = getSchoolGrades(selectedSchool.grade_range);
      setSelectedGrades(schoolGrades);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSchool, selectedSchool?.grade_range, schoolsEnrollmentData]);

  // Calculate community data similar to GeoXCommunityHeadlines
  const {
    populationTotals,
    currentVsPastChange,
  } = useMemo(() => {
    const totals = calculateGradeFilteredPopulation({
      esriData,
      selectedDriveTime,
      selectedGrades
    });
  
    const change = totals.past > 0 ? 
      ((totals.current - totals.past) / totals.past) * 100 : 0;
  
    return {
      populationTotals: totals,
      currentVsPastChange: change
    };
  }, [esriData, selectedDriveTime, selectedGrades]);

  const totalChange = useMemo(() => {
    return ((populationTotals.future - populationTotals.past) / populationTotals.past) * 100;
  }, [populationTotals]);

  // Market share calculation using calculateMarketShareStats
  const marketShareData = useMemo(() => {
    if (!selectedSchool || !schoolsEnrollmentData || !esriData) return null;
    
    const selectedSchoolData = schoolsEnrollmentData.find(d => d.uuid === selectedSchool.uuid);
    if (!selectedSchoolData) return null;

    // nearbySchools carries the baseline-eligibility verdict, so it has to be passed even
    // for the population calculation; without it a relocated school would still be handed a
    // baseline share measured around a campus it no longer occupies.
    const marketShareStats = calculateMarketShareStats({
      type: 'population',  // Use population-based calculation
      school: selectedSchool,
      schoolData: selectedSchoolData,
      selectedGrades,
      esriData,
      selectedDriveTime,
      nearbySchools
    });

    // Get population change
    const totals = calculateGradeFilteredPopulation({
      esriData,
      selectedDriveTime,
      selectedGrades
    });

    const populationChange = totals.current > 0 && totals.past > 0 ?
      ((totals.current - totals.past) / totals.past) * 100 : 0;

    return {
      current: marketShareStats.current,
      past: marketShareStats.past,
      change: marketShareStats.change,
      hasValidHistory: marketShareStats.hasValidHistory,
      baselineStatus: marketShareStats.baselineStatus,
      populationChange,
      metadata: {
        ...marketShareStats.metadata,
        populationTotals: {
          past: totals.past,
          current: totals.current
        }
      }
    };
  }, [selectedSchool, schoolsEnrollmentData, esriData, selectedGrades, selectedDriveTime, nearbySchools]);

  // Headlines data
  const populationHeadline = useMemo(() => ({
    title: 'Population Trends',
    content: `${Math.abs(populationTotals.current).toLocaleString()} school-age children live within a ${selectedDriveTime}-minute drive, which is ${currentVsPastChange >= 0 ? 'up' : 'down'} ${Math.abs(currentVsPastChange).toFixed(0)}% since 2020.`,
    trend: Math.abs(currentVsPastChange) < 5 ? 'neutral' : currentVsPastChange > 0 ? 'up' : 'down',
    metrics: [
      {
        label: 'Current Population',
        value: populationTotals.current.toLocaleString()
      },
      {
        label: `${ESRI_CENSUS_YEAR}-${String(ESRI_CURRENT_YEAR).slice(-2)} Change`,
        value: `${currentVsPastChange >= 0 ? '+' : ''}${Math.round(currentVsPastChange)}%`
      }
    ]
  }), [populationTotals, currentVsPastChange, selectedDriveTime]);

  // Projections headline
  const projectionsHeadline = useMemo(() => {
    // Check if we have any projections data at all
    if (!enrollmentProjections) {
      return {
        title: 'Projected Enrollment',
        content: 'Enrollment projection data is not available for this school.',
        trend: 'neutral',
        metrics: [
          { label: 'Current Enrollment', value: 'N/A' },
          { label: 'Future Projection', value: 'N/A' }
        ]
      };
    }

    const actualYears = Object.keys(enrollmentProjections?.actual_enrollment || {}).sort();
    const latestActualYear = actualYears[actualYears.length - 1];
    
    if (!latestActualYear || !enrollmentProjections?.actual_enrollment[latestActualYear]) {
      return {
        title: 'Projected Enrollment',
        content: 'Enrollment projection data is not available for this school.',
        trend: 'neutral',
        metrics: [
          { label: 'Current Enrollment', value: 'N/A' },
          { label: 'Future Projection', value: 'N/A' }
        ]
      };
    }
  
    const currentYearData = enrollmentProjections.actual_enrollment[latestActualYear];
    
    // Get all grades and filter out PreK
    const allGrades = Object.keys(currentYearData);
    const nonPreKGrades = allGrades
      .filter(grade => {
        const lowerGrade = grade.toLowerCase();
        return !(lowerGrade.includes('pre') || lowerGrade.includes('pk'));
      })
      .map(grade => {
        // Convert "Grade X" to "X" for numeric grades
        if (grade === 'Kindergarten') return grade;
        return grade.replace('Grade ', '');
      });
  
    // Calculate current year total
    const latestActualTotal = calculateEnrollment(currentYearData, nonPreKGrades);
  
    // Get projection data
    const projectionYears = Object.keys(enrollmentProjections?.projections?.median || {}).sort();
    const lastProjectionYear = projectionYears[projectionYears.length - 1];
    const projectionData = lastProjectionYear ? 
      enrollmentProjections.projections.median[lastProjectionYear] : null;
    
    // Calculate projection total using the same grade list
    const lastProjectionTotal = projectionData ? 
      calculateEnrollment(projectionData, nonPreKGrades) : null;
  
    // Get starting grade (including PreK if it exists)
    const startingGrade = allGrades.length > 0 ? 
      formatGrade(allGrades.sort((a, b) => {
        const aIsPK = a.toLowerCase().includes('pre') || a.toLowerCase().includes('pk');
        const bIsPK = b.toLowerCase().includes('pre') || b.toLowerCase().includes('pk');
        if (aIsPK) return -1;
        if (bIsPK) return 1;
        if (a === 'Kindergarten') return -1;
        if (b === 'Kindergarten') return 1;
        return a.localeCompare(b);
      })[0]) : null;
  
    return {
      title: 'Projected Enrollment',
      content: `${startingGrade ? `Starting with ${startingGrade}, ` : ''}View enrollment projections and growth scenarios for your school.`,
      trend: lastProjectionTotal && latestActualTotal ? 
            (lastProjectionTotal > latestActualTotal ? 'up' : 'down') : 'neutral',
      metrics: [
        {
          label: `${latestActualYear} Enrollment`,
          value: latestActualTotal?.toLocaleString() || 'N/A'
        },
        {
          label: `${lastProjectionYear} Projection`,
          value: lastProjectionTotal?.toLocaleString() || 'N/A'
        }
      ]
    };
  }, [enrollmentProjections]);

  const marketShareHeadline = useMemo(() => ({
    title: 'Market Share Analysis',
    content: selectedSchool ? `${formatSchoolName(selectedSchool.name)} enrolls ${
      selectedSchoolTrends?.selectedSchool?.newEnrollment?.toLocaleString() || 'N/A'
    } students, representing ${
      Math.round(marketShareData?.current) || 'N/A'
    }% of area children${
      marketShareData?.metadata?.isNewOrNewer
        ? ` (opened in ${selectedSchoolTrends?.selectedSchool?.timeframe?.endLabel || 'N/A'})`
        : marketShareData?.current != null && marketShareData?.past != null
          ? `, ${marketShareData.current > marketShareData.past ? 'up' : 'down'} from ${Math.round(marketShareData.past)}% in 2020` +
            (marketShareData.change != null ? ` (${marketShareData.change > 0 ? '+' : '-'}${Math.round(Math.abs(marketShareData.change))}% change)` : '')
          : ''
    }.` : 'Select a school to view market share analysis.',
    trend: !selectedSchool ? 'neutral' :
          !marketShareData?.change ? 'neutral' :
          marketShareData.change > 0 ? 'up' : 
          marketShareData.change < 0 ? 'down' : 'neutral',
    metrics: [
      {
        label: 'Current Share',
        value: `${marketShareData?.current != null ? Math.round(marketShareData.current) : 'N/A'}%`
      },
      {
        label: 'Share Change',
        value: !selectedSchool ? 'N/A' :
              marketShareData?.metadata?.isNewOrNewer
                ? 'N/A (newer school)'
                : marketShareData?.change != null
                  ? `${marketShareData.change > 0 ? '+' : '-'}${Math.round(Math.abs(marketShareData.change))}%`
                  // Naming the reason keeps a suppressed comparison from reading as missing data.
                  : BASELINE_STATUS_SHORT_LABELS[marketShareData?.baselineStatus] || 'N/A'
      }
    ]
  }), [selectedSchool, selectedSchoolTrends, marketShareData]);

  const comparisonHeadline = useMemo(() => {
    const { isDropAPinLocation } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);
    
    if (isDropAPinLocation) {
      // Calculate average enrollment change for all nearby schools
      const schoolsWithChange = schoolData.filter(school => 
        school.currentEnrollment && school.previousEnrollment && school.previousEnrollment > 0
      );
      
      const totalChange = schoolsWithChange.reduce((sum, school) => {
        const change = ((school.currentEnrollment - school.previousEnrollment) / school.previousEnrollment) * 100;
        return sum + change;
      }, 0);
      
      const avgChange = schoolsWithChange.length > 0 ? totalChange / schoolsWithChange.length : 0;
      
      return {
        title: 'Area School Comparison',
        content: selectedSchool ? 
          `The area around ${formatSchoolName(selectedSchool.name)} has ${schoolData.length} nearby schools with an average enrollment change of ${avgChange >= 0 ? '+' : ''}${Math.round(avgChange)}% between 2019-20 and 2024-25.` :
          'School comparison data not available.',
        trend: avgChange > 0 ? 'up' : avgChange < 0 ? 'down' : 'neutral',
        metrics: [
          {
            label: 'Enrollment Change',
            value: `${avgChange >= 0 ? '+' : ''}${Math.round(avgChange)}%`
          },
          {
            label: '# of Schools',
            value: schoolData.length.toString()
          }
        ]
      };
    } else {
      // Regular school logic
      const getRank = () => {
        if (!selectedSchool || !schoolData.length) return null;
        const sortedSchools = [...schoolData].sort((a, b) => {
          const changeA = a.currentEnrollment && a.previousEnrollment ? 
            ((a.currentEnrollment - a.previousEnrollment) / a.previousEnrollment) * 100 : -Infinity;
          const changeB = b.currentEnrollment && b.previousEnrollment ? 
            ((b.currentEnrollment - b.previousEnrollment) / b.previousEnrollment) * 100 : -Infinity;
          return changeB - changeA;
        });
        const rank = sortedSchools.findIndex(s => s.uuid === selectedSchool.uuid) + 1;
        return rank;
      };

      const rank = getRank();
      const percentChange = selectedSchoolTrends?.selectedSchool?.percentChange;
      const hasComparison = percentChange !== null && percentChange !== undefined;

      return {
        title: 'Area School Comparison',
        content: !selectedSchool
          ? 'School comparison data not available.'
          : hasComparison
            ? `${formatSchoolName(selectedSchool.name)}'s enrollment change of ${Math.round(percentChange)}% ranks ${rank}${getOrdinalSuffix(rank)} out of ${schoolData.length} nearby schools between 2019-20 and 2024-25.`
            : `${formatSchoolName(selectedSchool.name)} has no earlier enrollment on record, so its change cannot be ranked against the ${schoolData.length} nearby schools.`,
        trend: percentChange > 0 ? 'up' : 
              percentChange < 0 ? 'down' : 'neutral',
        metrics: [
          {
            label: 'Enrollment Change',
            value: hasComparison
              ? `${percentChange > 0 ? '+' : ''}${Math.round(percentChange)}%`
              : 'N/A (Newer School)'
          },
          {
            label: 'Area Rank',
            value: hasComparison && rank ? `${rank} of ${schoolData.length}` : 'N/A'
          }
        ]
      };
    }
  }, [selectedSchool, selectedSchoolTrends, schoolData, schoolsEnrollmentData]);

  const demographicsHeadline = useMemo(() => {
    const { isDropAPinLocation } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);
    
    if (isDropAPinLocation) {
      // Helper function to get largest demographic group from ESRI data
      const getESRIDemographics = (esriData, selectedDriveTime) => {
        const demographics = esriData?.drive_times?.[selectedDriveTime]?.racial_percentages?.child;
        if (!demographics) return null;

        // Map ESRI demographic fields to readable names (values are in decimal format, need to convert to percentage)
        const demographicMap = {
          'hispanic': 'Hispanic',
          'white': 'White', 
          'black': 'Black',
          'asian': 'Asian',
          'pacific_islander': 'Pacific Islander',
          'american_indian': 'American Indian',
          'two_or_more_races': 'Two or More Races',
          'other_races': 'Other Races'
        };

        // Find the largest group
        let largestGroup = null;
        let largestPercentage = 0;

        Object.entries(demographics).forEach(([key, value]) => {
          const percentage = (value || 0) * 100; // Convert from decimal to percentage
          if (percentage > largestPercentage && demographicMap[key]) {
            largestGroup = demographicMap[key];
            largestPercentage = percentage;
          }
        });

        return largestGroup && largestPercentage > 0 ? {
          group: largestGroup,
          percentage: Math.round(largestPercentage)
        } : null;
      };

      // Get ESRI demographics for largest child resident
      const esriDemographics = getESRIDemographics(esriData, selectedDriveTime);
      
      // Get enrollment demographics for largest demographic enrollment
      const enrollmentDemographics = calculateAreaDemographics({
        nearbySchools,
        schoolsEnrollmentData,
        selectedGrades,
        selectedDriveTime
      });

      return {
        title: 'Demographic Composition',
        content: selectedSchool ? 
          `The area around ${formatSchoolName(selectedSchool.name)} serves diverse demographics based on both residential and school enrollment data.` :
          'Demographic data not available.',
        trend: 'neutral',
        metrics: [
          {
            label: 'Area Demographics',
            value: esriDemographics ? 
              `${esriDemographics.group} (${esriDemographics.percentage}%)` : 
              'N/A'
          },
          {
            label: 'School Demographics',
            value: enrollmentDemographics.largestGroup || 'N/A'
          }
        ]
      };
    } else {
      // Regular school logic
      // Helper function to check if we have valid demographic comparison data
      const hasValidDemographicComparison = () => {
        if (!selectedSchoolTrends) return false;
        if (selectedSchoolTrends.hasDemographicData !== true) return false;
        if (!selectedSchoolTrends.largestGroup || selectedSchoolTrends.largestGroup === 'N/A') return false;
        if (typeof selectedSchoolTrends.demographicDifference !== 'number') return false;
        if (isNaN(selectedSchoolTrends.demographicDifference)) return false;
        return true;
      };

      const validComparison = hasValidDemographicComparison();

      return {
        title: 'Demographic Composition',
        content: validComparison ? 
          (() => {
            // Extra safety check to prevent malformed template strings
            const diff = selectedSchoolTrends.demographicDifference;
            const group = selectedSchoolTrends.largestGroup;
            
            if (typeof diff !== 'number' || isNaN(diff) || !group || group === 'N/A') {
              return `Demographic data is not available for ${formatSchoolName(selectedSchool?.name) || 'this school'}.`;
            }
            
            const percentage = Math.round(Math.abs(diff));
            const direction = diff >= 0 ? 'higher' : 'lower';
            
            return `The selected grades at this school serve a ${percentage}% ${direction} percentage of ${group} students compared to nearby schools.`;
          })() :
          `Demographic data is not available for ${formatSchoolName(selectedSchool?.name) || 'this school'}.`,
        trend: selectedSchoolTrends?.largestGroupChange > 0 ? 'up' : 
              selectedSchoolTrends?.largestGroupChange < 0 ? 'down' : 'neutral',
        metrics: [
          {
            label: 'Largest Group',
            value: selectedSchoolTrends?.largestGroup || 'N/A'
          },
          {
            label: '5-Year Change',
            value: selectedSchoolTrends?.largestGroupChange ? 
              `${selectedSchoolTrends.largestGroupChange > 0 ? '+' : ''}${Math.round(selectedSchoolTrends.largestGroupChange)}%` :
              selectedSchoolTrends?.hasDemographicData === false ? 
                'Not Available' :
                selectedSchoolTrends?.isNewerSchool ? 
                  'N/A (newer school)' : 
                  'N/A'
          }
        ]
      };
    }
  }, [selectedSchoolTrends, selectedSchool, schoolsEnrollmentData, esriData, selectedDriveTime, nearbySchools, selectedGrades]);

  // Final headlines array memo
  const headlines = useMemo(() => {
    const { isDropAPinLocation } = detectSchoolDataType(selectedSchool, schoolsEnrollmentData);
    
    const allHeadlines = [
      populationHeadline,
      projectionsHeadline,
      marketShareHeadline,
      comparisonHeadline,
      demographicsHeadline
    ];
    
    // For drop-a-pin locations, exclude projections (index 1) and market-share (index 2)
    if (isDropAPinLocation) {
      return allHeadlines.filter((_, index) => index !== 1 && index !== 2);
    }
    
    return allHeadlines;
  }, [
    populationHeadline,
    projectionsHeadline,
    marketShareHeadline,
    comparisonHeadline,
    demographicsHeadline,
    selectedSchool,
    schoolsEnrollmentData
  ]);

  // Determine if section should have animation
  const getShouldAnimateSection = (sectionIndex) => {
    const sectionKey = getInsightSection(sectionIndex);
    return chartAnimationState.shouldAnimate(sectionKey, selectedSchool?.uuid);
  };

  // Helper function to check if selected headline corresponds to a specific section type
  const isSelectedSection = (sectionType) => {
    if (selectedHeadline === null) return false;
    const sectionKey = getInsightSection(selectedHeadline);
    return sectionKey === sectionType;
  };

  const handleHeadlineSelect = useCallback((index) => {
    if (selectedHeadline === index) return;
    
    // Clear loading states for previous section
    const prevSection = getInsightSection(selectedHeadline);
    setSectionLoadingStates(prev => ({
      ...prev,
      [prevSection]: { loading: false, metricsLoading: false }
    }));
    
    // Set loading for new section only if needed
    const newSection = getInsightSection(index);
    if (!isSectionDataLoaded(index)) {
      setSectionLoadingStates(prev => ({
        ...prev,
        [newSection]: { loading: true, metricsLoading: true }
      }));
    }
    
    setSelectedHeadline(index);
    setToolAndSection('school-summary', newSection);
  }, [selectedHeadline, setToolAndSection, isSectionDataLoaded, getInsightSection]);
  
  const handleSchoolChange = useCallback((event) => {
    const schoolId = event.target.value;
    // Handle both UUID and NCES ID for backward compatibility  
    const school = schools.find(s => s.uuid === schoolId || s.ncessch === schoolId);
    if (school) {
      // Reset to Population Trends headline when changing schools
      setSelectedHeadline(0);
      // Reset section loading states
      setSectionLoadingStates({
        population: { loading: false, metricsLoading: false },
        projections: { loading: false, metricsLoading: false },
        marketShare: { loading: false, metricsLoading: false },
        comparison: { loading: false, metricsLoading: false },
        demographics: { loading: false, metricsLoading: false }
      });
      // Update the tool and section in the Insight context
      setToolAndSection('school-summary', 'population');
      // Select the new school
      useSchoolStore.getState().selectSchool(school);
    }
  }, [schools, setToolAndSection]);
  
  const handleDriveTimeChange = useCallback((event) => {
    setSelectedDriveTime(Number(event.target.value));
  }, []);

  const handleTravelModeChange = useCallback((event) => {
    changeTravelMode(event.target.value);
  }, [changeTravelMode]);

  const handleSchoolSelect = useCallback((uuid) => {
    setFocusedSchool(prev => {
      // If clicking the same school or selected school, deselect
      if (prev?.uuid === uuid || selectedSchool?.uuid === uuid) {
        return null;
      }
      // Find the school in schoolData and set as focused
      return schoolData.find(s => s.uuid === uuid) || null;
    });
  }, [schoolData, selectedSchool]);

  const handleGovernanceFilterChange = (event) => {
    const value = event.target.value;
    setGovernanceFilter(typeof value === 'string' ? value.split(',') : value);
  };
  
  const handleSchoolTypeFilterChange = (event) => {
    const value = event.target.value;
    setSchoolTypeFilter(typeof value === 'string' ? value.split(',') : value);
  };

  // Memoize visibleDriveTimes array to prevent unnecessary rerenders
  const visibleDriveTimes = useMemo(() => [selectedDriveTime], [selectedDriveTime]);

  const metricsData = useMemo(() => {
    if (!selectedSchool) return null;
    
    return {
      populationTotals,
      currentVsPastChange,
      marketShareData,
      selectedSchoolTrends,
      enrollmentProjections,
      schoolData,
      filteredSchools
    };
  }, [
    selectedSchool,
    populationTotals,
    currentVsPastChange,
    marketShareData,
    selectedSchoolTrends,
    enrollmentProjections,
    schoolData,
    filteredSchools
  ]);
  
  useEffect(() => {
    if (selectedSchool?.uuid && selectedDriveTime === 10 && metricsData) {
      setSchoolMetrics(selectedSchool.uuid, metricsData);
    }
  }, [selectedSchool?.uuid, selectedDriveTime, metricsData, setSchoolMetrics]);

  useEffect(() => {
    if (selectedSchool?.uuid) {
      trackUsage('school_summary_viewed', { schoolUuid: selectedSchool.uuid });
    }
  }, [selectedSchool?.uuid, trackUsage]);

  return (
    <PageLayout
      data-tool="school-summary"
      title="School Summary"
      description={selectedSchool
        ? "A comprehensive overview of your school's performance and context"
        : "Get a quick snapshot of key metrics for any school. Start by selecting a school."
      }
    >
      {/* Initial loading state for schools list */}
      <LoadingErrorWrapper
        loading={schoolsLoading}
        error={error}
        initialLoading={!schools || !schools.length}
        hideSteps={true}
      >
        {!selectedSchool ? (
          <InitialSchoolSelector
            schools={schools}
            selectedSchool={selectedSchool}
            onSchoolChange={handleSchoolChange}
          />
        ) : (
          <Box>
            {/* Filters always visible */}
            <SchoolSummaryFilters
              schools={schools}
              selectedSchool={selectedSchool}
              selectedDriveTime={selectedDriveTime}
              selectedTravelMode={selectedTravelMode}
              availableDriveTimes={availableDriveTimes}
              availableTravelModes={availableTravelModes}
              governanceFilter={governanceFilter}
              schoolTypeFilter={schoolTypeFilter}
              onSchoolChange={handleSchoolChange}
              onDriveTimeChange={handleDriveTimeChange}
              onTravelModeChange={handleTravelModeChange}
              onGovernanceFilterChange={handleGovernanceFilterChange}
              onSchoolTypeFilterChange={handleSchoolTypeFilterChange}
            />
            
            {/* AI Overview Summary */}
            <Box sx={{ mt: 3 }}>
              <AISummaryAccordion
                selectedSchool={selectedSchool}
                driveTime={selectedDriveTime}
                summaryType="combined"
                travelMode={selectedTravelMode}
                defaultExpanded={true}
              />
            </Box>
  
            <Box sx={{ mt: 4 }}>
              <Grid 
                container 
                spacing={0}
                sx={{
                  display: 'flex',
                  flexDirection: { xs: 'column', lg: 'row' },
                  width: '100%',
                  margin: 0,
                  flexWrap: 'nowrap',
                  minWidth: 0
                }}
              >

                {/* Headlines Column - Always visible */}
                <Grid 
                  item 
                  xs={12} 
                  lg="auto" 
                  sx={{
                    width: { lg: '360px' }, 
                    flexShrink: 0,
                    pr: { lg: 2 },
                    minWidth: { lg: '360px' }
                  }}
                >
                  {schoolsLoading ? (
                    <HeadlinesColumnSkeleton />
                  ) : (
                    <Box sx={{ 
                      '& > :not(:last-child)': {
                        mb: 2
                      }
                    }}>
                      {headlines.map((headline, index) => {
                        // Combine loading states - should be loading if either metrics or full content is loading
                        const isLoadingAny = areMetricsLoading(index) || isSectionLoading(index);
                          
                        return (
                          <HeadlineBoxWithLoading
                            key={index}
                            title={headline.title}
                            trend={headline.trend}
                            metrics={headline.metrics}
                            isSelected={selectedHeadline === index}
                            onClick={() => handleHeadlineSelect(index)}
                            isLoading={isLoadingAny}
                          />
                        );
                      })}
                    </Box>
                  )}
                </Grid>
  
                {/* Visualizations Column */}
                <Grid 
                  item 
                  xs={12}
                  lg  
                  sx={{
                    flexGrow: 1,
                    width: '100%',
                    mt: { xs: 3, lg: 1 },
                    minWidth: 0,
                    overflow: 'visible'
                  }}
                >
                  {/* Population Section */}
                  {isSelectedSection('population') && (
                    isSectionLoading(selectedHeadline) ? (
                      <PopulationTrendsSkeleton />
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        width: '100%',
                        pt: 2
                      }}>
                        <PopulationAnalysis
                          selectedSchool={selectedSchool}
                          populationTotals={populationTotals}
                          currentVsPastChange={currentVsPastChange}
                          selectedDriveTime={selectedDriveTime}
                          selectedGrades={selectedGrades}
                          user={user}
                        />
                        
                        <Box sx={{ 
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 0,
                          width: '100%',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <GeoXCommunityTrends
                            populationTotals={populationTotals}
                            totalChange={totalChange}
                            selectedDriveTime={selectedDriveTime}
                            animationEnabled={getShouldAnimateSection(0)}
                            cardProps={CARD_PROPS_NO_ELEVATION}
                          />
                        </Box>

                        <Box sx={{ 
                          mt: 2,
                          display: 'flex',
                          justifyContent: 'center'
                        }}>
                          <Typography variant="caption" color="text.secondary">
                            Source: {' '}
                            <Link 
                              href="https://doc.arcgis.com/en/esri-demographics/latest/reference/methodologies.htm" 
                              target="_blank" 
                              rel="noopener noreferrer"
                            >
                              Esri
                            </Link>
                          </Typography>
                        </Box>
                      </Box>
                    )
                  )}
  
                  {/* Projections Section - Hidden for drop-a-pin locations */}
                  {isSelectedSection('projections') && (
                    isSectionLoading(selectedHeadline) ? (
                      <ProjectionsSkeleton />
                    ) : (
                      <Box sx={{ 
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        pt: 2
                      }}>
                        <ProjectionsAnalysis
                          selectedSchool={selectedSchool}
                          enrollmentData={enrollmentProjections}
                          user={user}
                        />
                        
                        <Box sx={{ 
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          p: 2,
                          mt: -1,
                          width: '100%',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          <Box sx={{ mb: 3 }}>
                            <Box sx={{ 
                              display: 'flex', 
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              mb: 1
                            }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <AutoGraphIcon sx={{ color: 'primary.main' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  Enrollment Trends & Projections
                                </Typography>
                              </Box>
                              {enrollmentProjections && (
                                <IconButton
                                  onClick={() => {
                                    import('../../utils/enrollmentProjectionsDownload').then(({ downloadProjectionsCSV }) => {
                                      downloadProjectionsCSV(enrollmentProjections, selectedSchool?.name || 'school');
                                    });
                                  }}
                                  sx={{ 
                                    color: 'primary.main',
                                    '&:hover': {
                                      backgroundColor: 'primary.light',
                                      color: 'white'
                                    }
                                  }}
                                  title="Download enrollment projections data"
                                >
                                  <GetApp />
                                </IconButton>
                              )}
                            </Box>
                            <Chip
                              icon={<InfoOutlinedIcon sx={{ fontSize: 16 }} />}
                              label={`Data source: ${enrollmentProjections?.based_on_user_data ? 
                                "Historical and user-inputted data" : 
                                "Historical fall count day enrollment only"}`}
                              color="primary"
                              variant="outlined"
                              size="small"
                            />
                          </Box>

                          <Box sx={{ 
                            display: 'flex', 
                            flexDirection: { xs: 'column', lg: 'row' },
                            gap: 3,
                            width: '100%',
                            minWidth: 0
                          }}>
                            <Box sx={{ 
                              flex: { xs: '1 1 100%', lg: '1 1 70%' },
                              minWidth: 0
                            }}>
                              <ProjectXProjectionsChart
                                school={selectedSchool}
                                enrollmentData={enrollmentProjections}
                                selectedGrades={selectedGrades}
                                timeframe="5-year"
                                view="school-level"
                                targets={EMPTY_TARGETS}
                                selectedScenarios={DEFAULT_SCENARIOS}
                              />
                            </Box>
                          </Box>
                        </Box>

                        <Box sx={{ 
                          mt: 2,
                          display: 'flex',
                          justifyContent: 'center'
                        }}>
                          <Typography variant="caption" color="text.secondary">
                            Source: {' '}
                            <Link href="https://nces.ed.gov/ccd/" target="_blank" rel="noopener noreferrer">
                              NCES 2019-20 through 2024-25
                            </Link>
                          </Typography>
                        </Box>
                      </Box>
                    )
                  )}
  
                  {/* Market Share Section - Hidden for drop-a-pin locations */}
                  {isSelectedSection('market-share') && (
                    isSectionLoading(selectedHeadline) ? (
                      <MarketShareSkeleton />
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        width: '100%',
                        pt: 2
                      }}>
                        <MarketShareAnalysis
                          selectedSchool={selectedSchool}
                          selectedSchoolTrends={selectedSchoolTrends}
                          marketShareData={marketShareData}
                          esriData={esriData}
                          selectedGrades={selectedGrades}
                          selectedDriveTime={selectedDriveTime}
                          schoolsEnrollmentData={schoolsEnrollmentData}
                          user={user}
                        />
                        
                        <Box sx={{ 
                          p: 0,
                          width: '100%',
                          minWidth: 0,
                          overflow: 'hidden'
                        }}>
                          {selectedSchool ? (
                            <CompXCommunityPlot
                              selectedSchool={selectedSchool}
                              marketShareData={marketShareData}
                              currentVsPastChange={marketShareData?.populationChange}
                              schoolCategory={selectedSchoolTrends?.category}
                              timeframeData={selectedSchoolTrends?.selectedSchool?.timeframe}
                            />
                          ) : (
                            <Typography>No school selected</Typography>
                          )}
                        </Box>

                        <Box sx={{ 
                          mt: 2,
                          display: 'flex',
                          justifyContent: 'center'
                        }}>
                          <Typography variant="caption" color="text.secondary">
                            Sources: {' '}
                            <Link href="https://www.esri.com/en-us/home" target="_blank" rel="noopener noreferrer">
                              Esri
                            </Link>
                            {' and '}
                            <Link href="https://nces.ed.gov/ccd/" target="_blank" rel="noopener noreferrer">
                              NCES 2019-20 through 2024-25
                            </Link>
                          </Typography>
                        </Box>
                      </Box>
                    )
                  )}
  
                  {/* Comparison Section */}
                  {isSelectedSection('comparison') && (
                    isSectionLoading(selectedHeadline) ? (
                      <ComparisonSkeleton />
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        width: '100%',
                        pt: 2
                      }}>
                        <ComparisonAnalysis
                          selectedSchool={selectedSchool}
                          selectedSchoolTrends={selectedSchoolTrends}
                          schoolData={schoolData}
                          nearbySchools={nearbySchools}
                          schoolsEnrollmentData={schoolsEnrollmentData}
                          selectedGrades={selectedGrades}
                          selectedDriveTime={selectedDriveTime}
                          user={user}
                        />
                        
                        <Box sx={{ 
                          display: 'flex',
                          flexDirection: { xs: 'column', lg: 'row' },
                          gap: 3,
                          width: '100%',
                          minWidth: 0
                        }}>
                          <Box sx={{ 
                            flex: { xs: '1 1 100%', lg: '1 1 70%' },
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 2
                          }}>
                            <Box sx={{ mb: 3 }}>
                              <Box sx={{ 
                                display: 'flex', 
                                alignItems: 'center',
                                gap: 1,
                                mb: 1
                              }}>
                                <MapsHomeWorkIcon sx={{ color: 'primary.main' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, color: 'text.primary' }}>
                                  School Locations
                                </Typography>
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                  bgcolor: 'rgba(25, 118, 210, 0.1)',
                                  color: 'primary.main',
                                  p: 1,
                                  borderRadius: 1,
                                  fontWeight: 500
                                }}
                              >
                                Click on map markers to view detailed school information
                              </Typography>
                            </Box>

                            <Box sx={{ 
                              height: 400,
                              position: 'relative',
                              overflow: 'hidden'
                            }}>
                              <UnifiedSchoolMap
                                mode="geographic"
                                selectedSchool={selectedSchool}
                                filteredSchools={schoolData}
                                esriData={esriData}
                                visibleDriveTimes={visibleDriveTimes}
                                schoolsEnrollmentData={schoolsEnrollmentData}
                                governanceFilter={governanceFilter}
                                selectedGrades={selectedGrades}
                                schoolTypeFilter={schoolTypeFilter}
                                PopupComponent={GeoXNearbyMapPopup}
                                focusedSchool={focusedSchool}
                                onSchoolSelect={handleSchoolSelect}
                                initialZoom={12}
                              />
                            </Box>
                          </Box>

                          <Box sx={{ 
                            flex: { xs: '1 1 100%', lg: '1 1 50%' },
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 2,
                            height: 550,
                            overflow: 'hidden'
                          }}>
                            <SimplePopulationTable 
                              schools={schoolData}
                              selectedSchoolTrends={selectedSchoolTrends}
                              focusedSchool={focusedSchool}
                              selectedSchool={selectedSchool}
                              onSchoolSelect={handleSchoolSelect}
                            />
                          </Box>
                        </Box>

                        <Box sx={{ 
                          mt: 2,
                          display: 'flex',
                          justifyContent: 'center'
                        }}>
                          <Typography variant="caption" color="text.secondary">
                            Source: {' '}
                            <Link href="https://nces.ed.gov/ccd/" target="_blank" rel="noopener noreferrer">
                              NCES 2019-20 through 2024-25
                            </Link>
                          </Typography>
                        </Box>
                      </Box>
                    )
                  )}
  
                  {/* Demographics Section */}
                  {isSelectedSection('demographics') && (
                    isSectionLoading(selectedHeadline) ? (
                      <DemographicsSkeleton />
                    ) : (
                      <Box sx={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        width: '100%',
                        pt: 2
                      }}>
                        <DemographicAnalysis
                          selectedSchool={selectedSchool}
                          selectedSchoolTrends={selectedSchoolTrends}
                          nearbySchools={nearbySchools}
                          schoolsEnrollmentData={schoolsEnrollmentData}
                          selectedGrades={selectedGrades}
                          selectedDriveTime={selectedDriveTime}
                          esriData={esriData}
                          user={user}
                        />
                        
                        <Box sx={{ 
                          display: 'grid', 
                          gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, 
                          gap: 3,
                          mt: -2
                        }}>
                          <Box sx={{ 
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 1,
                            p: 0,
                            mt: 2
                          }}>
                            <GeoXCommunityDemographics
                              esriData={esriData}
                              selectedDriveTime={selectedDriveTime}
                              animationEnabled={getShouldAnimateSection(0)}
                              cardProps={CARD_PROPS_NO_BORDER}
                            />
                          </Box>
                          <Box sx={{ p: 2 }}>
                            <GeoXEnrollmentCompositionChanges
                              selectedSchool={selectedSchool}
                              schoolsEnrollmentData={schoolsEnrollmentData}
                              filteredSchools={filteredSchools}
                              selectedGrades={selectedGrades}
                              governanceFilter={governanceFilter}
                              timeframe={selectedSchoolTrends?.selectedSchool?.timeframe}
                              animationEnabled={getShouldAnimateSection(0)}
                            />
                          </Box>
                        </Box>

                        <Box sx={{ 
                          mt: 2,
                          display: 'flex',
                          justifyContent: 'center'
                        }}>
                          <Typography variant="caption" color="text.secondary">
                            Sources: {' '}
                            <Link href="https://www.esri.com/en-us/home" target="_blank" rel="noopener noreferrer">
                              Esri
                            </Link>
                            {' and '}
                            <Link href="https://nces.ed.gov/ccd/" target="_blank" rel="noopener noreferrer">
                              NCES 2019-20 through 2024-25
                            </Link>
                          </Typography>
                        </Box>
                      </Box>
                    )
                  )}

                </Grid>
              </Grid>
            </Box>
          </Box>
        )}
      </LoadingErrorWrapper>
    </PageLayout>
  );
};

const SchoolSummaryWithErrorHandler = withIndexedDBErrorHandler(SchoolSummary);
export default SchoolSummaryWithErrorHandler;